import type { FunctionRole, Segment } from '@projektverwaltung/shared';
import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { api } from './api.js';

export type SessionUser = {
  user_id: string;
  email: string;
  name: string;
  /** Einzige Entscheidungs-Achse für Admin-Rechte (geteilte Matrix). */
  is_admin: boolean;
  is_test_user: boolean;
  function_roles: { role: FunctionRole; scope_segment: Segment | null }[];
  mode: 'dev-stub' | 'azure-ad';
};

type SessionContextValue = {
  user: SessionUser | null;
  signInAs: (user: SessionUser) => void;
  signOut: () => void;
};

const STORAGE_KEY = 'pv.session';

const SessionContext = createContext<SessionContextValue | null>(null);

function loadFromStorage(): SessionUser | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<SessionUser>;
    // Schema-Wechsel mit Phase G: alte Sessions ohne function_roles/is_test_user verwerfen.
    // B5: is_admin wird ebenfalls geprüft — fehlendes is_admin (Stale-Session) → verwerfen,
    // damit User sauber neu einloggt (fail-safe: kein Admin-Recht ohne explizites is_admin).
    if (
      typeof parsed.user_id !== 'string' ||
      typeof parsed.email !== 'string' ||
      typeof parsed.name !== 'string' ||
      !Array.isArray(parsed.function_roles) ||
      typeof parsed.is_test_user !== 'boolean' ||
      typeof parsed.is_admin !== 'boolean'
    ) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed as SessionUser;
  } catch {
    localStorage.removeItem(STORAGE_KEY);
    return null;
  }
}

export function getSessionUserId(): string | null {
  const u = loadFromStorage();
  return u?.user_id ?? null;
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<SessionUser | null>(() => loadFromStorage());

  useEffect(() => {
    if (user) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(user));
      // Dev-Stub: Identität auch als Cookie, damit Voll-Seiten-Navigationen
      // (MCP-OAuth-Connect/Callback) den User mitführen — dort geht der
      // x-dev-user-id-Header verloren. In Azure-AD-Modus ignoriert das Backend
      // das Cookie (nutzt die Session-Cookie); daher unschädlich.
      document.cookie = `pv_dev_user_id=${encodeURIComponent(user.user_id)}; path=/; SameSite=Lax; max-age=86400`;
    } else {
      localStorage.removeItem(STORAGE_KEY);
      document.cookie = 'pv_dev_user_id=; path=/; SameSite=Lax; max-age=0';
    }
  }, [user]);

  // Muster A: kein lokaler User → versuche Azure-Session-Cookie zu hydrieren.
  // Im Dev-Stub-Modus liefert /v1/auth/me 401 → bleibt null (Dropdown-Login).
  useEffect(() => {
    if (user) return;
    let cancelled = false;
    void api
      .authMe()
      .then((me) => {
        if (!cancelled) setUser({ ...me });
      })
      .catch(() => {
        /* keine Azure-Session — Dev-Stub-Login greift */
      });
    return () => {
      cancelled = true;
    };
    // nur einmal beim Mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const signInAs = useCallback((u: SessionUser) => setUser(u), []);
  const signOut = useCallback(() => {
    void api.logout().catch(() => {
      /* Cookie ggf. schon weg */
    });
    setUser(null);
  }, []);

  const value = useMemo(() => ({ user, signInAs, signOut }), [user, signInAs, signOut]);

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionContextValue {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession must be used within <SessionProvider>');
  return ctx;
}
